import { post } from 'packages/lib/utils/legacy-tsapi/rest-helpers';
import { apiFetch } from 'packages/lib/api/annotations/rest';
import { withRetry } from 'packages/lib/utils/retry-wrapper';
import {
  ServiceLevelIndicatorMetric,
  TechMetric,
  UserPerfMetric,
} from 'packages/lib/metrics/types';

// Apr-07-2026 Generated by Gemini 2.5 PRO via CLI

/** Set to true and deploy to beta (no metrics on local/staging) for helpful test console logging */
const DEBUG_LOGGING = false;

const DB_NAME = 'metrics-db';
const STORE_NAME = 'unsent-metrics';
const DB_VERSION = 1;

type Metric =
  | Omit<UserPerfMetric, 'attributes'>
  | ServiceLevelIndicatorMetric
  | TechMetric;

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { autoIncrement: true });
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      console.error('IndexedDB error:', request.error);
      reject(request.error);
    };
  });
};

export const saveMetrics = async (metrics: Metric[], type: 'user' | 'tech') => {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.add({ type, metrics });

    return new Promise<void>((resolve, reject) => {
      transaction.oncomplete = async () => {
        if (DEBUG_LOGGING) {
          console.log('Saved metrics to local DB: ', metrics);
          void getSavedMetricsCount().then((count) => {
            console.log('Number of saved metrics batches: ', count);
          });
        }
        resolve();
      };
      transaction.onabort = () => {
        console.error('Transaction aborted', transaction.error);
        reject(transaction.error);
      };
      transaction.onerror = () => {
        console.error('Transaction error', transaction.error);
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.error('Failed to save metrics to IndexedDB:', error);
    return Promise.reject(error);
  }
};

interface MetricItem {
  type: 'user' | 'tech';
  metrics: Metric[];
}

export const getAndClearUnsentMetrics = async (): Promise<
  { key: IDBValidKey; value: MetricItem }[]
> => {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const allMetricsReq = store.getAll();
    const allKeysReq = store.getAllKeys();

    const allMetrics = await new Promise<MetricItem[]>((resolve, reject) => {
      allMetricsReq.onsuccess = () => resolve(allMetricsReq.result);
      allMetricsReq.onerror = () => reject(allMetricsReq.error);
    });

    const allKeys = await new Promise<IDBValidKey[]>((resolve, reject) => {
      allKeysReq.onsuccess = () => resolve(allKeysReq.result);
      allKeysReq.onerror = () => reject(allKeysReq.error);
    });

    if (allMetrics.length > 0) {
      store.clear();
    }

    return allKeys.map((key, index) => ({ key, value: allMetrics[index] }));
  } catch (error) {
    console.error('Failed to get and clear unsent metrics:', error);
    return [];
  }
};

export const getSavedMetricsCount = async (): Promise<number> => {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const countRequest = store.count();

    return new Promise((resolve, reject) => {
      countRequest.onsuccess = () => {
        resolve(countRequest.result);
      };
      countRequest.onerror = () => {
        console.error('Failed to get metrics count:', countRequest.error);
        reject(countRequest.error);
      };
    });
  } catch (error) {
    console.error('Failed to open DB for metrics count:', error);
    return -1;
  }
};

export const resendSavedMetrics = async () => {
  const unsentMetrics = await getAndClearUnsentMetrics();
  if (unsentMetrics.length === 0) {
    return;
  }

  for (const { value: item } of unsentMetrics) {
    const { type, metrics } = item;
    try {
      if (type === 'user') {
        // `metrics` is a batch of up to 25 user metrics
        await withRetry(() =>
          apiFetch('userMetrics')('', 'POST', JSON.stringify(metrics))
        );
        if (DEBUG_LOGGING) console.log('Resent saved user metrics: ', metrics);
      } else if (type === 'tech') {
        // `metrics` is a batch of tech metrics
        await post('techMetrics', '/metrics', metrics, undefined, false);
        if (DEBUG_LOGGING) console.log('Resent saved tech metrics: ', metrics);
      }
    } catch (error) {
      console.error(
        'Failed to resend metrics, saving them back to IndexedDB:',
        error
      );
      // If sending fails again, save them back.
      await saveMetrics(metrics, type);
    }
  }
};
